import { FC, memo } from 'react';
import moment from 'moment/moment';
import { useResponsive } from '../../../../hooks';
import {
  BlockWrapper,
  GrayBox,
  ExecutorWrapper,
  DateLine,
  Dot,
  ExecutorIconWrapper,
} from './info.styled';
import { Text, VStack } from '../../components-v4';
import { removeHtmlTags } from '../../utils';
import { IPublicRequestData } from '../../request-single-page-slice';
import { ReactComponent as ExecutorIcon } from '../../assets/user-check.svg';

type Props = {
  requestData: IPublicRequestData;
};

export const Info: FC<Props> = memo(({ requestData }) => {
  const { viewport } = useResponsive();
  const hasExecutionTime =
    requestData.time_of_execution_start && requestData.time_of_execution_end;

  if (requestData.status === 'run') {
    const isOneDay = moment(requestData.time_of_execution_start).isSame(
      requestData.time_of_execution_end,
      'day'
    );

    return (
      <BlockWrapper>
        <Text
          variant={viewport.mobile ? 'heading-5' : 'heading-4'}
          color="gray.500"
        >
          Исполнитель назначен
        </Text>
        <ExecutorWrapper>
          <ExecutorIconWrapper isMobile={viewport.mobile}>
            <ExecutorIcon />
          </ExecutorIconWrapper>
          <VStack spacing="none" justify="center">
            {hasExecutionTime &&
              (isOneDay ? (
                <DateLine>
                  <Text
                    variant={viewport.mobile ? 'body-14' : 'body-16'}
                    color="gray.800"
                  >
                    {moment(requestData.time_of_execution_start).format(
                      'DD.MM.YYYY'
                    )}
                  </Text>
                  <Dot />
                  <Text
                    variant={viewport.mobile ? 'body-14' : 'body-16'}
                    color="gray.800"
                  >
                    {moment(requestData.time_of_execution_start).format('HH:mm')}{' '}
                    — {moment(requestData.time_of_execution_end).format('HH:mm')}
                  </Text>
                </DateLine>
              ) : (
                <DateLine>
                  <Text
                    variant={viewport.mobile ? 'body-14' : 'body-16'}
                    color="gray.800"
                  >
                    {moment(requestData.time_of_execution_start).format(
                      'DD.MM.YYYY/HH:mm'
                    )}{' '}
                    —{' '}
                    {moment(requestData.time_of_execution_end).format(
                      'DD.MM.YYYY/HH:mm'
                    )}
                  </Text>
                </DateLine>
              ))}
            <Text
              variant={viewport.mobile ? 'body-14' : 'body-16'}
              color="gray.500"
            >
              {hasExecutionTime
                ? 'Плановое время визита'
                : 'Время визита будет назначено позже'}
            </Text>
          </VStack>
        </ExecutorWrapper>
      </BlockWrapper>
    );
  }

  if (requestData.status === 'performed') {
    const isOneDay = moment(requestData.time_of_execution_start).isSame(
      requestData.time_of_execution_end,
      'day'
    );

    return (
      <GrayBox>
        <BlockWrapper>
          <Text
            variant={viewport.mobile ? 'heading-5' : 'heading-4'}
            color="gray.500"
          >
            Дата и время выполнения
          </Text>
          {isOneDay ? (
            <DateLine>
              <Text
                variant={viewport.mobile ? 'body-14' : 'body-16'}
                color="gray.800"
              >
                {moment(requestData.time_of_execution_start).format(
                  'DD.MM.YYYY'
                )}
              </Text>
              <Dot />
              <Text
                variant={viewport.mobile ? 'body-14' : 'body-16'}
                color="gray.800"
              >
                {moment(requestData.time_of_execution_start).format('HH:mm')} —{' '}
                {moment(requestData.time_of_execution_end).format('HH:mm')}
              </Text>
            </DateLine>
          ) : (
            <DateLine>
              <Text
                variant={viewport.mobile ? 'body-14' : 'body-16'}
                color="gray.800"
              >
                {moment(requestData.time_of_execution_start).format(
                  'DD.MM.YYYY/HH:mm'
                )}{' '}
                —{' '}
                {moment(requestData.time_of_execution_end).format(
                  'DD.MM.YYYY/HH:mm'
                )}
              </Text>
            </DateLine>
          )}
        </BlockWrapper>
        <BlockWrapper>
          <Text
            variant={viewport.mobile ? 'heading-5' : 'heading-4'}
            color="gray.500"
          >
            Выполненные работы
          </Text>
          <Text
            variant={viewport.mobile ? 'body-14' : 'body-16'}
            color="gray.800"
          >
            {removeHtmlTags(requestData.work_description || '')}
          </Text>
        </BlockWrapper>
      </GrayBox>
    );
  }

  if (requestData.status === 'delayed') {
    return (
      <GrayBox>
        <BlockWrapper>
          <Text
            variant={viewport.mobile ? 'heading-5' : 'heading-4'}
            color="gray.500"
          >
            Причина переноса
          </Text>
          <Text
            variant={viewport.mobile ? 'body-14' : 'body-16'}
            color="gray.800"
          >
            {removeHtmlTags(requestData.reason_of_transfer || '')}
          </Text>
        </BlockWrapper>
      </GrayBox>
    );
  }

  if (['refusal', 'abandonment'].includes(requestData.status)) {
    return (
      <GrayBox>
        <BlockWrapper>
          <Text
            variant={viewport.mobile ? 'heading-5' : 'heading-4'}
            color="gray.500"
          >
            Причина отказа
          </Text>
          <Text
            variant={viewport.mobile ? 'body-14' : 'body-16'}
            color="gray.800"
          >
            {removeHtmlTags(requestData.reason_of_abandonment || '')}
          </Text>
        </BlockWrapper>
      </GrayBox>
    );
  }

  return null;
});
